import { holidayWindows, type Holiday, type HoursSchedule, type HoursScheduleInput } from "./configuration.js";

/**
 * The one reading of a schedule (#265): the lander makes it at call time
 * in the customer's account and the portal shows the same answer as
 * "right now", so the two can never disagree. A holiday is a closed
 * window in the schedule's zone - `from <= now < to` on local wall time -
 * and outside every window the weekly windows decide.
 */
export interface HoursReading {
  open: boolean;
  reason: "open" | "closed" | "holiday";
  /** The window that closed it, when `reason` is `holiday`. */
  holiday?: Holiday;
}

/** Local wall-clock parts in a zone, without a date library. An unknown zone reads as UTC rather than throwing on a live call. */
export function localWallTime(timezone: string, now: Date): { day: number; minutes: number; stamp: string } {
  const options: Intl.DateTimeFormatOptions = { weekday: "short", hour: "2-digit", minute: "2-digit", year: "numeric", month: "2-digit", day: "2-digit", hourCycle: "h23" };
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: timezone });
  } catch {
    fmt = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" });
  }
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value]));
  const hour = Number(parts["hour"]) % 24;
  const minute = Number(parts["minute"]);
  return {
    day: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(String(parts["weekday"])),
    minutes: hour * 60 + minute,
    stamp: `${parts["year"]}-${parts["month"]}-${parts["day"]}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
  };
}

/** The holiday window `stamp` (local `YYYY-MM-DDTHH:MM`) falls in, if any. A row still in the one-day shape is read as its windows. */
export function activeHoliday(holidays: NonNullable<HoursScheduleInput["holidays"]> | undefined, stamp: string): Holiday | undefined {
  for (const h of holidays ?? []) {
    for (const w of holidayWindows(h)) if (w.from <= stamp && stamp < w.to) return w;
  }
  return undefined;
}

function within(minutes: number, opens: string, closes: string): boolean {
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const from = toMin(opens);
  const to = toMin(closes);
  // A window that ends before it starts runs past midnight.
  return to >= from ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

export function evaluateHours(schedule: HoursSchedule | HoursScheduleInput, now: Date): HoursReading {
  const { day, minutes, stamp } = localWallTime(schedule.timezone, now);
  const holiday = activeHoliday(schedule.holidays, stamp);
  if (holiday) return { open: false, reason: "holiday", holiday };
  const open = schedule.weekly.some((w) => w.day === day && within(minutes, w.opens, w.closes));
  return open ? { open: true, reason: "open" } : { open: false, reason: "closed" };
}
